import type {
  ExplorationResponse,
  LedgerResponse,
  PlaytimeResponse,
  RecipesResponse,
  ResetSettings,
  ResetSettingsResponse,
  TimersResponse,
} from '@brimstone/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export function useTimers() {
  return useQuery({ queryKey: ['timers'], queryFn: () => api<TimersResponse>('/api/timers') });
}

export function usePlaytime(weeks = 8) {
  return useQuery({
    queryKey: ['playtime', weeks],
    queryFn: () => api<PlaytimeResponse>(`/api/playtime?weeks=${weeks}`),
  });
}

export function useLedger(days = 90) {
  return useQuery({
    queryKey: ['ledger', days],
    queryFn: () => api<LedgerResponse>(`/api/ledger?days=${days}`),
  });
}

export function useReset() {
  return useQuery({
    queryKey: ['reset'],
    queryFn: () => api<ResetSettingsResponse>('/api/settings/reset'),
  });
}

/** Saves the weekly reset; everything bucketed by week is refetched. */
export function useSaveReset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reset: ResetSettings) =>
      api<ResetSettingsResponse>('/api/settings/reset', { method: 'PUT', json: { reset } }),
    onSuccess: (res) => {
      qc.setQueryData(['reset'], res);
      void qc.invalidateQueries({ queryKey: ['playtime'] });
      void qc.invalidateQueries({ queryKey: ['chronicle'] });
      void qc.invalidateQueries({ queryKey: ['tonight'] });
    },
  });
}

export function useExploration() {
  return useQuery({
    queryKey: ['exploration'],
    queryFn: () => api<ExplorationResponse>('/api/exploration'),
  });
}

export function useRecipes() {
  return useQuery({ queryKey: ['recipes'], queryFn: () => api<RecipesResponse>('/api/recipes') });
}
